import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators, ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { catchError, EMPTY, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { apiErrors } from 'src/app/common/ApiError';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { ProfileRole, ProfileRoleConstants } from 'src/app/common/ProfileRole';
import { ProfileStatus, ProfileStatusConstants } from 'src/app/common/ProfileStatus';
import { AppProfileService } from 'src/app/services/profile.service';
import { CommonModule, Location } from '@angular/common';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { FormRowComponent } from '../form-row/form-row.component';
import { UserListMode } from '../shop-user-list/user-list.component';

/** The backend's answer when the profile is its customer's last approved admin (backend #704). */
export const PROFILE_LAST_ADMIN = 'profileLastAdmin';

/**
 * Edits a profile's role and status, reached from „Benutzer" (`users`) or from „Registrierungen"
 * (`registrations`) — the two used to be separate components that differed in one guard (#199).
 *
 * Name and e-mail are shown, not edited: the update takes subject, status and role only
 * (`ProfileUpdateCommand`), so edits to them used to be dropped without a word.
 */
/**
 * Role and status of one profile; name and e-mail are shown only. Typed from here (#199).
 *
 * The two editable controls are named as the backend names the request properties (`Status`,
 * `Role`): `applyServerFieldErrors` places a coded 400 by the last segment of its `field`, so
 * `profileStatusInvalid` / `profileRoleInvalid` land under the dropdown they are about.
 */
function buildProfileForm(fb: FormBuilder, profile: ExtendedProfile) {
  return fb.group({
    id: [profile.id, [Validators.required, Validators.maxLength(50)]],
    fullName: [{ value: profile.user.fullName, disabled: true }],
    email: [{ value: profile.user.email, disabled: true }],
    status: [profile.status, [Validators.required]],
    role: [profile.role, [Validators.required]],
  });
}
type ProfileForm = ReturnType<typeof buildProfileForm>;

@Component({
    imports: [DropDownsModule, ReactiveFormsModule, CommonModule, AsyncClickDirective, FormRowComponent],
    selector: 'app-shop-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.css']
})
export class UserEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  profileService = inject(AppProfileService);
  private location = inject(Location);
  private callApiService = inject(CallApiService);

  private destroyRef = inject(DestroyRef);

  public readonly mode: UserListMode = inject(ActivatedRoute).snapshot.data['userListMode'] ?? 'users';
  public profileForm: ProfileForm;
  public profile: ExtendedProfile = new ExtendedProfile();
  public roles: ProfileRole[] = [];
  public states: ProfileStatus[] = [];
  /**
   * The customer's last approved administrator keeps both: the backend refuses to change either
   * (`ProfileUpdateCommand.ValidateForUpdate`), so the fields say so before the save does.
   * This replaces a guard that locked role and status for every profile that was *not* an admin.
   *
   * Decided on load for the signed-in user's own customer, and on the backend's 409
   * `profileLastAdmin` for everyone else (a carrier editing a foreign customer, backend #704).
   */
  public isLastAdmin = false;

  ngOnInit() {
    this.roles = this.profileService.getProfileRoles();
    this.states = this.profileService.getProfileStates();
    const id = String(this.activatedRoute.snapshot.paramMap.get('id'));

    this.profileService
      .getById(id)
      .pipe(
        tap((profile) => {
          this.profile = profile;
          this.profileForm = buildProfileForm(this.fb, profile);
        }),
        switchMap((profile) => this.lastAdminOf(profile)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((isLastAdmin) => {
        if (isLastAdmin) {
          this.lockAsLastAdmin();
        }
      });
  }

  /**
   * Whether `profile` is its customer's only approved admin. Only answerable for the signed-in
   * user's own customer — the list of a carrier's other customers is not at hand — so for those
   * the backend's refusal is the guard, as it always was.
   */
  private lastAdminOf(profile: ExtendedProfile): Observable<boolean> {
    if (
      profile.role !== ProfileRoleConstants.RoleIdAdmin ||
      profile.status !== ProfileStatusConstants.StatusIdNameApproved
    ) {
      return of(false);
    }
    return this.profileService.currentProfile$.pipe(
      take(1),
      switchMap((me) =>
        me?.customer?.id !== profile.customer?.id
          ? of(false)
          : this.profileService.getAllOfCustomerList().pipe(
              map(
                (profiles) =>
                  !profiles.some(
                    (p) =>
                      p.id !== profile.id &&
                      p.role === ProfileRoleConstants.RoleIdAdmin &&
                      p.status === ProfileStatusConstants.StatusIdNameApproved
                  )
              ),
              // Without the list, leave the decision to the backend rather than lock the form.
              catchError(() => of(false))
            )
      )
    );
  }

  /**
   * Shows the stored role and status, locked, with the hint that says why. After a refused save
   * the dropdowns would otherwise keep showing the very values the backend just turned down.
   */
  private lockAsLastAdmin(): void {
    this.isLastAdmin = true;
    this.profileForm.controls.status.reset(this.profile.status);
    this.profileForm.controls.role.reset(this.profile.role);
    this.profileForm.controls.status.disable();
    this.profileForm.controls.role.disable();
  }

  submitForm(): Observable<unknown> {
    if (!this.profileForm.valid) {
      return EMPTY;
    }
    // A copy: on a refusal `profile` still says what the server holds, which the lock relies on.
    const update: ExtendedProfile = {
      ...this.profile,
      id: this.profileForm.controls.id.value,
      status: this.profileForm.controls.status.value,
      role: this.profileForm.controls.role.value,
    };

    // The form goes along so a coded 400 is placed at its field; a code that names none —
    // `profileLastAdmin` among them — is worded by the dictionary in the toast instead of the
    // generic line, and anything untranslated still gets the generic line (#178).
    return this.callApiService
      .callWithToasts$(
        this.profileService.update(update),
        $localize`Benutzer wurde aktualisiert.`,
        $localize`Beim Aktualisieren des Benutzers ist ein Fehler aufgetreten.`,
        this.profileForm
      )
      .pipe(
        tap(() =>
          this.router.navigateByUrl(this.mode === 'registrations' ? 'shop-registration-list' : 'shop-user-list')
        ),
        catchError((err) => {
          // What the page could not know on load for a foreign customer, it knows now.
          if (apiErrors(err).some((e) => e.code === PROFILE_LAST_ADMIN)) {
            this.lockAsLastAdmin();
          }
          return throwError(() => err);
        })
      );
  }

  onBackClicked() {
    this.location.back();
  }
}
